import React from "react";
import PropTypes from "prop-types";
import { AlertCircle } from "@opendate-app/setlist";
import { formatDateLabel } from "../scheduleBuilder";
import { landsAt } from "../seriesActivities";

const SHOWN_DATES = 4;
const ON_SALE_COLUMN = "public_on_sale_time";

const startTimeOf = (row) => row.showTime || (row.times || [])[0];

const sentence = (names) => (names.length < 3 ? names.join(" and ") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`);

const ShowActivityTimingTable = ({ activities, generated, timeZone }) => {
  if (activities.length === 0 || generated.length === 0) return null;

  const shown = generated.slice(0, SHOWN_DATES);
  const fixed = activities.filter((activity) => activity.timing.at);
  const more = generated.length - shown.length;
  const onSaleFixed = fixed.some((activity) => activity.column === ON_SALE_COLUMN);
  const otherFixed = fixed.some((activity) => activity.column !== ON_SALE_COLUMN);

  return (
    <div className="series-review-timing" data-testid="review-timing">
      <div className="series-review-timing-header">
        <div className="series-review-timing-title">Show activities by date</div>
        <div className="series-review-timing-subtitle">
          Each date's announce, on-sale and other times, worked out from its start time.
        </div>
      </div>
      <div className="series-review-timing-scroll">
        <table className="series-review-timing-table">
          <thead>
            <tr>
              <th>Date</th>
              {activities.map((activity) => (
                <th key={activity.name}>
                  {activity.name}
                  {activity.timing.at ? " · same for all" : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => (
              <tr key={row.id}>
                <td>{formatDateLabel(row.date)}</td>
                {activities.map((activity) => {
                  const at = landsAt(activity.timing, row.date, startTimeOf(row), timeZone);
                  const sameDay = at.format("YYYY-MM-DD") === row.date;
                  return (
                    <td key={activity.name} className={activity.timing.at ? "series-review-timing-fixed" : ""}>
                      {at.format(sameDay ? "h:mm A" : "MMM D, h:mm A")}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(more > 0 || fixed.length > 0) && (
        <div className="series-review-timing-footer">
          {more > 0 && (
            <div className="series-review-timing-more">
              + {more} more {more === 1 ? "date" : "dates"}
            </div>
          )}
          {fixed.length > 0 && (
            <div className="series-notice warning">
              <AlertCircle width={20} height={20} aria-hidden="true" className="series-notice-icon" />
              <div>
                <div className="series-notice-title">
                  {sentence(fixed.map((activity) => activity.name))} {fixed.length === 1 ? "is" : "are"} the same for
                  all {generated.length} {generated.length === 1 ? "date" : "dates"}
                </div>
                {onSaleFixed && (
                  <div>
                    Every date goes on sale at the same moment. That's right for a season launch; switch to Each date to open
                    each date on its own schedule.
                  </div>
                )}
                {otherFixed && <div>Every date shares this time. Switch to Each date to give every date its own.</div>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

ShowActivityTimingTable.propTypes = {
  activities: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, column: PropTypes.string, timing: PropTypes.object })).isRequired,
  generated: PropTypes.arrayOf(PropTypes.object).isRequired,
  timeZone: PropTypes.string,
};

export default ShowActivityTimingTable;
